import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useNotification } from 'components/Notification'
import { upsertCustomerUploadInstruction } from 'services/customerUploadInstructions'

const DEFAULT_QUERY_PARAMS = {
  id: null,
  customer: null,
  instructions: undefined,
  urls: [],
}

const useCustomerRequestInstructionsModalForm = (customer, instructionsToEdit) => {
  const [params, setParams] = useState(DEFAULT_QUERY_PARAMS)
  const queryClient = useQueryClient()
  const isEditing = !!instructionsToEdit?.id

  const { createNotification } = useNotification()

  const { mutate: upsertCustomerInstruction, isFetching: isSubmitting } = useMutation({
    mutationFn: params => upsertCustomerUploadInstruction(params),
    onSuccess: () => {
      setParams({ ...DEFAULT_QUERY_PARAMS, customer })
      queryClient.invalidateQueries({ queryKey: ['fetchCustomerInstructions', customer.id] })
      createNotification({
        message: `${
          isEditing ? 'Successfully updated instruction' : 'Successfully created instruction'
        }`,
        variant: 'success',
      })
    },
    onError: () => {
      createNotification({
        messaage: 'Unable to create customer instruction',
        variant: 'error',
      })
    },
  })

  const handleInstructionChange = ({ target }) => {
    setParams(prev => ({ ...prev, instructions: target.value }))
  }

  const handleSubmit = (handleCloseFn, resetFileStateFn) => {
    const formattedParams = {
      id: params.id,
      customerId: params.customer.id,
      instructions: params.instructions,
      urls: params.urls,
    }

    upsertCustomerInstruction(formattedParams)

    handleCloseFn(resetFileStateFn)
  }

  // If the customer has changed, then the change has to be
  // reflected in the query params used for the mutation
  useEffect(() => {
    setParams(prev => ({ ...prev, customer }))
  }, [customer])

  // anytime a user is editing an instruction, then we want the
  // existing information to be reflected in the form.
  useEffect(() => {
    setParams(prev => ({
      ...prev,
      id: instructionsToEdit?.id,
      instructions: instructionsToEdit?.instructions || undefined,
      urls: instructionsToEdit?.filePaths || [],
    }))
  }, [instructionsToEdit])

  return {
    params,
    setParams,
    isEditing,
    isSubmitting,
    handleInstructionChange,
    handleSubmit,
  }
}

export default useCustomerRequestInstructionsModalForm
